import { Edit, Plus, Save } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "../../../../components/button";
import { Dropdown } from "../../../../components";
import { SearchableSelect, SelectSearchOption } from "../../../../components/searchable-select";
import { RequiredLabel } from "../../../../components/form/RequiredLabel";
import { OpportunityHierarchyItem, ProgramHierarchyItem } from "../../types";
import {
  OPPORTUNITY_TYPE_LABELS,
  OPPORTUNITY_TYPE_OPTIONS,
  OPPORTUNTY_TYPE,
  normalizeOpportunityType,
} from "../../types/campaign-status.constants";
import { useOpportunitySources } from "../../hooks/useOpportunitySources";

export const opportunityFormSchema = z.object({
  type: z.nativeEnum(OPPORTUNTY_TYPE, {
    error: () => ({ message: "Opportunity type is required." }),
  }),

  sourceId: z.string().trim().min(1, "Source is required."),
  campaignId: z.string().optional(),
  programId: z.string().optional(),
  eventId: z.string().optional(),
  surveyId: z.string().optional(),
});

export type OpportunityFormValues = z.infer<typeof opportunityFormSchema>;

interface OpportunityEditorProps {
  selectedProgram: ProgramHierarchyItem | null;
  selectedOpportunity: OpportunityHierarchyItem | null;
  saving?: boolean;
  initialType?: OPPORTUNTY_TYPE;
  lockType?: boolean;
  compact?: boolean;
  onCancel?: () => void;
  onSubmit: (values: OpportunityFormValues) => void | Promise<void>;
}

export function OpportunityEditor({
  selectedProgram,
  selectedOpportunity,
  saving = false,
  initialType = OPPORTUNTY_TYPE.Event,
  lockType = false,
  compact = false,
  onCancel,
  onSubmit,
}: OpportunityEditorProps) {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<OpportunityFormValues>({
    defaultValues: {
      sourceId: "",
      type: initialType,
    },
    resolver: zodResolver(opportunityFormSchema),
  });

  const selectedType = watch("type");
  const {
    data: sourceOptions,
    isLoading: sourceOptionsLoading,
    search: searchSourceOptions,
  } = useOpportunitySources(selectedType, selectedOpportunity?.id);

  const handleSubmitOpportunity = async (values: OpportunityFormValues) => {
    values.campaignId = selectedProgram?.campaignId ?? "";
    values.programId = selectedProgram?.id ?? "";

    await onSubmit(values);
    reset({
      sourceId: "",
      type: initialType,
    });
  };

  useEffect(() => {
    reset({
      sourceId: selectedOpportunity?.eventId || selectedOpportunity?.surveyId || "",
      type: selectedOpportunity ? normalizeOpportunityType(selectedOpportunity.type) : initialType,
    });
  }, [initialType, reset, selectedOpportunity]);

  return (
    <form
      className={
        compact
          ? "space-y-5"
          : "rounded-2xl border border-[#DDEBE3] bg-white p-5 shadow-[0px_10px_24px_rgba(0,66,50,0.05)]"
      }
      onSubmit={handleSubmit(handleSubmitOpportunity)}
    >
      {!compact ? (
        <div className="mb-6 flex items-start gap-3 border-b border-[#E6EFE9] pb-4">
          <div className="rounded-xl bg-[#E8FFF0] p-2.5 text-[#00A86B]">
            {selectedOpportunity ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#004232]">
              {selectedOpportunity ? "Edit Opportunity" : "Create Opportunity"}
            </h3>
            <p className="mt-1 text-sm text-[#6A7282]">
              {selectedOpportunity == null
                ? "Create a new opportunity for the selected program."
                : "Update the selected opportunity."}
            </p>
          </div>
        </div>
      ) : null}

      <div className={compact ? "w-full max-w-none space-y-5" : "max-w-4xl space-y-5"}>
        <div className={lockType ? "space-y-2" : "grid gap-5 md:grid-cols-[16rem_minmax(0,1fr)]"}>
          {!lockType ? (
            <div className="space-y-2">
              <RequiredLabel required>Type</RequiredLabel>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <Dropdown
                    value={field.value}
                    options={OPPORTUNITY_TYPE_OPTIONS.map((option) => ({
                      label: OPPORTUNITY_TYPE_LABELS[option],
                      value: option,
                    }))}
                    placeholder="Select type"
                    onValueChange={(value) => field.onChange(value as OPPORTUNTY_TYPE)}
                    disabled={saving}
                    triggerClassName="h-11 rounded-xl"
                  />
                )}
              />
              {errors.type ? (
                <p className="text-xs text-red-600">{errors.type.message?.toString()}</p>
              ) : null}
            </div>
          ) : null}

          <div className="w-full space-y-2">
            <RequiredLabel required>Opportunity</RequiredLabel>
            <Controller
              control={control}
              name="sourceId"
              render={({ field }) => (
                <SearchableSelect<SelectSearchOption>
                  value={field.value}
                  options={sourceOptions}
                  placeholder="Search opportunity"
                  emptyMessage="No opportunity found."
                  disabled={saving}
                  className="h-11 w-full rounded-xl"
                  onValueChange={field.onChange}
                  searchHint={
                    sourceOptionsLoading ? "Loading opportunities..." : "Search opportunity by name"
                  }
                  onSearchChange={searchSourceOptions}
                />
              )}
            />
            {errors.sourceId ? (
              <p className="text-xs text-red-600">{errors.sourceId.message?.toString()}</p>
            ) : null}
          </div>
        </div>
      </div>

      <div
        className={`${compact ? "" : "mt-6"} flex justify-end gap-2 border-t border-[#E6EFE9] pt-4`}
      >
        {onCancel ? (
          <Button
            variant="outline"
            type="button"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
        ) : null}
        <Button
          variant="primary"
          type="submit"
          className="min-w-40 gap-2"
          disabled={saving}
        >
          {selectedOpportunity ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {selectedOpportunity ? "Save Opportunity" : "Create Opportunity"}
        </Button>
      </div>
    </form>
  );
}
